import { FC, useEffect, useState } from 'react';

import SharedModal from '../SharedModal';
import { useTrackUserEvent } from '../../../../utils/api/requests/useMutations';
import { UserEventTypes } from '../../../../interfaces/UserEventInterface';
import { AppPlatforms } from '../../../../API';
import { callEvent } from '../../../../utils/Amplitude/AmplitudeUtilities';
import { useLocationStore } from '../../../../stores/locationStore';
import OnboardingModalBody from './OnboardingModalBody';
import { themeColors, themeSizes } from '../../../../styles/globalStyleVars';
import { TypographyStyled } from '../../../Styled/Typography';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import useMediaQuery from '../../../../hooks/useMediaQuery';

interface OnboardingModalProps {
  hasEquipmentPermission: boolean;
  isOnboarded?: boolean;
  onClose: () => void;
  setIsOnboarded?: (showOnboarding: boolean) => void;
  show: boolean;
  showWhatsNewFeatureFlag?: boolean;
  showOnboardingModal: (showModal: boolean) => void;
}

const OnboardingModal: FC<OnboardingModalProps> = ({
  hasEquipmentPermission,
  isOnboarded = false,
  onClose,
  setIsOnboarded,
  show,
  showWhatsNewFeatureFlag = false,
  showOnboardingModal,
}) => {
  const { activeLocation } = useLocationStore();
  const breakpoints = useBreakpoints();
  const isSmAndDown = useMediaQuery(breakpoints.down('sm'));
  const isLgAndUp = useMediaQuery(breakpoints.up('lg'));
  const [onboardingComplete, setOnboardingComplete] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const { mutate: markUserOnboarded } = useTrackUserEvent(
    UserEventTypes.COMPLETE_ONBOARDING,
    { platforms: [AppPlatforms.WEB] }
  );

  useEffect(() => {
    if (show) {
      setShowModal(true);
    }
  }, [show]);

  useEffect(() => {
    showOnboardingModal(showModal);
  }, [showModal]);

  const setUserOnboarded = () => {
    // Run onboarding status mutation if user hasn't viewed the onboarding before
    if (!isOnboarded || !showWhatsNewFeatureFlag) {
      markUserOnboarded();
      setIsOnboarded && setIsOnboarded(true);
    }
  };
  const closeModal = () => {
    onClose();
    setUserOnboarded();
    setOnboardingComplete(true);
    setShowModal(false);
    callEvent(
      onboardingComplete ? 'onboarding complete' : 'onboarding cancelled',
      {
        locationNum: activeLocation,
        path: window.location.href,
        featureType: 'onboarding',
      }
    );
  };

  // Modal content horizontal padding: 24px mobile, 32px md, 64px lg+.
  const contentPaddingInline = (() => {
    if (isSmAndDown) return themeSizes.lg;
    if (isLgAndUp) return '64px';
    return themeSizes.xl;
  })();

  return (
    <SharedModal
      contentStyle={{ paddingInline: contentPaddingInline }}
      dataTestId='onboarding-modal'
      onClose={closeModal}
      show={showModal}
      showFooter={false}
      size={'lg'}
      text={{
        body: (
          <OnboardingModalBody
            hasEquipmentPermission={hasEquipmentPermission}
            setShowModal={setShowModal}
          />
        ),
        header: (
          <TypographyStyled
            text='Support at your finger tips'
            color={themeColors.colorGray_7}
            as='span'
            variant={isSmAndDown ? 'h4' : 'h2'}
            data-testid={'onboarding-title'}
          />
        ),
      }}
    />
  );
};

export default OnboardingModal;
